import React, { useEffect, useState } from 'react';
import {ReactComponent as Icon } from '../../assets/icons/Edit.svg'
import {useDisclosure } from '@mantine/hooks';
import TableMedusa from '../../components/Table';
import {CategoryListProps, CategoryRowType} from "../../types/categories";
import { Table, Button } from '@mantine/core';
import {ReactComponent as RemoveIcon} from '../../assets/icons/Remove.svg'
import PopUp from '../../components/PopUp';
import CategoryPopUp from './CategoryPopUp';
import api from '../../api/axiosClient';
import { useCategories } from '../../context/CategoriesContext';

interface TextileTypeOptionResponse {
    id: number;
    name: string;
    code?: string;
}


const CategoryList = ({data, activeTab, onDelete, onSave}: CategoryListProps) => {
    const icon = <Icon width={19} height={19}></Icon>;
    const removeIcon = <RemoveIcon width={19} height={19}></RemoveIcon>;
    const [editCategory, setEditCategory] = useState<CategoryRowType | null>(null);
    const [deleteCategoryId, setDeleteCategoryId] = useState<number | null>(null);
    const [opened, { open, close }] = useDisclosure(false);
    const [openedDel, handlerDel] = useDisclosure(false);
    const [textileCodes, setTextileCodes] = useState<Record<number, string>>({});
    const { similarColors } = useCategories();

    let table = null;

    useEffect(() => {
        const fetchTextileTypes = async () => {
            try {
                const response = await api.get<TextileTypeOptionResponse[]>(`/textil-types/options`);
                const codeMap = response.data.reduce<Record<number, string>>((acc, item) => {
                    acc[item.id] = item.code || item.name;
                    return acc;
                }, {});
                setTextileCodes(codeMap);
            } catch (error) {
                console.error('Error fetching textile types in category list:', error);
                setTextileCodes({});
            }
        };

        fetchTextileTypes();
    }, []);

    const onClickDetail = (id:number)=>{
        const category = data?.find((item) => item.id === id) || null;
        setEditCategory(category);
        open();
    }

    const onClickRemove = (id:number)=>{
        setDeleteCategoryId(id);
        handlerDel.open();
    }

    const onClickDelete = () => {
        if (deleteCategoryId !== null) {
            onDelete(deleteCategoryId);
        }
        setDeleteCategoryId(null);
        handlerDel.close();
    }

    const handleSave = async (category: any) => {
        let saved;
        if (onSave) {
            saved = await onSave(category);
        }
        close();
        return saved;
    }

    useEffect(() => {
        if (!editCategory || !data) {
            return;
        }

        const updatedCategory = data.find((item) => item.id === editCategory.id) || null;
        if (updatedCategory && updatedCategory !== editCategory) {
            setEditCategory(updatedCategory);
        }
    }, [data, editCategory]);

    const sortedData = [...(data ?? [])].sort(
            (a, b) => (a.id ?? 0) - (b.id ?? 0)
    );

    const colorNameById = sortedData.reduce<Record<number, string>>((acc, item) => {
        acc[item.id] = item.name;
        return acc;
    }, {});

    const formatConversion = (category: CategoryRowType) => {
        if (!category.details || category.details.length === 0) {
            return '-';
        }

        return category.details
            .map(({ textil_type_id, conv_value }) => `${textileCodes[textil_type_id] || textil_type_id}${conv_value}%`)
            .join(' ');
    };

    if (activeTab === 'Color') {
        const rows = sortedData.map(({ id, name }) => (
            <Table.Tr key={id}>
                <Table.Td>{id}</Table.Td>
                <Table.Td>{name}</Table.Td>
                <Table.Td>{(similarColors[id] ?? []).map((similarId) => colorNameById[similarId] ?? similarId).join(', ') || '-'}</Table.Td>
                <Table.Td>
                    <Button variant="transparent" onClick={()=>onClickDetail(id)}>{icon}</Button>
                    <Button variant="transparent" onClick={()=>onClickRemove(id)}>{removeIcon}</Button>
                </Table.Td>
            </Table.Tr>
        ));
        table = <TableMedusa headers={["ID", "Name", "Similar", ""]} rows={rows}></TableMedusa>
     } else if (activeTab === 'Reuse') {
        const rows = sortedData.map(({ id, name }) => (
            <Table.Tr key={id}>
                <Table.Td>{id}</Table.Td>
                <Table.Td>{name}</Table.Td>
                <Table.Td>
                    <Button variant="transparent" onClick={()=>onClickDetail(id)}>{icon}</Button>
                    <Button variant="transparent" onClick={()=>onClickRemove(id)}>{removeIcon}</Button>
                </Table.Td>
            </Table.Tr>
        ));
        table = <TableMedusa headers={["ID", "Name", ""]} rows={rows}></TableMedusa>
     } else if (activeTab === 'Recycling') {
        const rows = sortedData.map((category) => (
            <Table.Tr key={category.id}>
                <Table.Td>{category.id}</Table.Td>
                <Table.Td>{category.name}</Table.Td>
                <Table.Td>{formatConversion(category)}</Table.Td>
                <Table.Td>{category.enable ? "Yes" : "No"}</Table.Td>
                <Table.Td>
                    <Button variant="transparent" onClick={()=>onClickDetail(category.id)}>{icon}</Button>
                    <Button variant="transparent" onClick={()=>onClickRemove(category.id)}>{removeIcon}</Button>
                </Table.Td>
            </Table.Tr>
        ));
        table = <TableMedusa headers={["ID", "Name", "Conversion", "Enable", ""]} rows={rows}></TableMedusa>
     }

     return(
            <div>
                <CategoryPopUp opened={opened} close={close} title={`Edit ${activeTab} Category`} onSave={handleSave} category={editCategory} categories={data ?? []}></CategoryPopUp>
                <PopUp opened={openedDel} close={handlerDel.close} size={700} title="Warning" onSave={onClickDelete} button1="Delete" >Are you sure you want to delete this category?</PopUp>
                {table}
            </div>
     )

}

export default CategoryList;
